import React, { useEffect, useState } from "react";
import { withRouter } from "next/router";
import { WithRouterProps } from "next/dist/client/with-router";
import Logo from "../../../../public/images/logo-new.svg";
import { InfoMessage, TextField } from "../..";
import { ImageContainer, Title, TitleContainer, CustomSubmitButton, FieldsContainer } from "../BasicInfo/BasicInfo.styles";
import {
  StyledForm,
  PasswordRule,
  PasswordRuleContainer,
  PasswordRuleIcon,
  PasswordRuleText,
} from "./Password.styles";
import { ResetPasswordInterface } from "../../../models/Login";
import { resetPasswordAdmin } from "../../../services/AuthenticationService";

interface PasswordProps extends WithRouterProps {
  resetPasswordInitialData: ResetPasswordInterface;
  goToNextStep: Function
}

const Password: React.FC<PasswordProps> = ({
  resetPasswordInitialData,
  router,
  goToNextStep
}) => {
  const [resetPasswordData, setResetPasswordData] = useState<ResetPasswordInterface>(resetPasswordInitialData);
  const [loading, setLoading] = useState<boolean>(false);
  const [resetPasswordError, setResetPasswordError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    router.replace({
      pathname: router.pathname,
      query: {},
    });
  }, []);

  const atLeastOneLetter = /^.*[A-Za-z].*$/;
  const atLeastOneNumber = /^.*\d.*$/;
  const atLeastOneSpecialCharacter = /^.*[!@#$%^&*=?].*$/;
  const between8and16CharactersLong = /^.{8,16}$/;

  const [isAtLeastOneLetter, setIsAtLeastOneLetter] = useState<boolean>(
    atLeastOneLetter.test(resetPasswordData.user.password)
  );
  const [isAtLeastOneNumber, setIsAtLeastOneNumber] = useState<boolean>(
    atLeastOneNumber.test(resetPasswordData.user.password)
  );
  const [isAtLeastOneSpecialCharacter, setIsAtLeastOneSpecialCharacter] =
    useState<boolean>(
      atLeastOneSpecialCharacter.test(resetPasswordData.user.password)
    );
  const [isBetween8and16CharactersLong, setIsBetween8and16CharactersLong] =
    useState<boolean>(
      between8and16CharactersLong.test(resetPasswordData.user.password)
    );

  const isActionButtonDisabled = () => {
    return !(
      isAtLeastOneLetter &&
      isAtLeastOneNumber &&
      isAtLeastOneSpecialCharacter &&
      isBetween8and16CharactersLong
    );
  };
  
  const handleEnterSubmit = ((event: React.FormEvent) => {
    event.preventDefault();
    if (!isActionButtonDisabled) {
      handlePasswordReset()
    }
  });

  const handlePasswordReset = () => {
    setLoading(true);
    setResetPasswordError(false);

    resetPasswordAdmin(resetPasswordData).then((res) => {
      setLoading(false);

      if (res.status === 200) {
        goToNextStep();
      } else if (res.status === 400) {
        setErrorMessage(res.errors?.message?.[0]);
        setResetPasswordError(true);
      } else {
        setErrorMessage("Oops! It looks like there's a hiccup. Please try again or contact support if the issue persists.");
        setResetPasswordError(true);
      }
    })
  };

  const validatePassword = (passwd: string) => {
    setIsAtLeastOneLetter(atLeastOneLetter.test(passwd));
    setIsAtLeastOneNumber(atLeastOneNumber.test(passwd));
    setIsAtLeastOneSpecialCharacter(atLeastOneSpecialCharacter.test(passwd));
    setIsBetween8and16CharactersLong(between8and16CharactersLong.test(passwd));
  };

  const onPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setResetPasswordData((prevResetPasswordData) => ({
      ...prevResetPasswordData,
      user: {
        ...prevResetPasswordData.user,
        password: event.target.value,
      },
    }));
    validatePassword(event.target.value);
  };

  return (
    <StyledForm onSubmit={handleEnterSubmit}>
      <ImageContainer>
        <Logo />
      </ImageContainer>
      <TitleContainer>
        <Title>Create New Password</Title>
      </TitleContainer>
      <FieldsContainer>
        <TextField
          data-cy="register-password"
          autoFocus
          fullWidth
          type="password"
          placeholder="Create Password"
          size="lg"
          value={resetPasswordData.user.password}
          onChange={onPasswordChange}
        />
        {resetPasswordError && (
          <InfoMessage
            data-cy="register-error"
            variant="error"
            hasBackground={true}
            message={errorMessage}
          />
        )}
      </FieldsContainer>
      <PasswordRuleContainer>
        <PasswordRule className={isAtLeastOneLetter ? "is-checked" : ""}>
          <PasswordRuleIcon
            data-cy="register-password-one-letter"
            icon={isAtLeastOneLetter ? "Check" : "Plus"}
          />
          <PasswordRuleText> At least one letter </PasswordRuleText>
        </PasswordRule>
        <PasswordRule className={isAtLeastOneNumber ? "is-checked" : ""}>
          <PasswordRuleIcon
            data-cy="register-password-one-number"
            icon={isAtLeastOneNumber ? "Check" : "Plus"}
          />
          <PasswordRuleText> At least one number </PasswordRuleText>
        </PasswordRule>
        <PasswordRule
          className={isAtLeastOneSpecialCharacter ? "is-checked" : ""}
        >
          <PasswordRuleIcon
            data-cy="register-password-one-special-character"
            icon={isAtLeastOneSpecialCharacter ? "Check" : "Plus"}
          />
          <PasswordRuleText>
            {" "}
            At least one special character (!@#$%^&*=?){" "}
          </PasswordRuleText>
        </PasswordRule>
        <PasswordRule
          className={isBetween8and16CharactersLong ? "is-checked" : ""}
        >
          <PasswordRuleIcon
            data-cy="register-password-length"
            icon={isBetween8and16CharactersLong ? "Check" : "Plus"}
          />
          <PasswordRuleText>
            {" "}
            Be between 8 - 16 characters long{" "}
          </PasswordRuleText>
        </PasswordRule>
      </PasswordRuleContainer>
      <CustomSubmitButton
        onClick={() => handlePasswordReset()}
        disabled={isActionButtonDisabled()}
        disabledStateStyling="lowOpacity"
        loading={loading}
        type="submit"
      >
        Create Password & Login
      </CustomSubmitButton>
    </StyledForm>
  );
};

Password.displayName = "Password";

const ComponentWithRouter = withRouter(Password);

export { ComponentWithRouter as Password };
